import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import schoolsCsv from '../data/schools_utf_8.csv';
import { getUserAcademyName, getUserDisplayName } from '../api/auth';
import { AcademyMember, membersAPI, normalizeMembersPayload } from '../api/members';
import { useAuth } from '../context/AuthContext';

type AdminSortKey = 'name' | 'phone';
type StudentSortKey = 'name' | 'schoolName' | 'memberClass' | 'memo';
type SortDirection = 'asc' | 'desc';
type MemberRole = 'admin' | 'student';

type SchoolRecord = {
  code: string;
  schoolName: string;
  location: string;
};

type EditableMember = {
  key: string;
  tauserId: string;
  role: MemberRole;
  email: string;
  name: string;
  password: string;
  academyname: string;
  phone: string;
  parentPhone: string;
  memberClass: string;
  memo: string;
  location: string;
  schoolName: string;
  schoolCode: string;
};

type AdminMemberRow = {
  editable: EditableMember;
  name: string;
  phone: string;
};

type StudentMemberRow = {
  editable: EditableMember;
  name: string;
  schoolName: string;
  memberClass: string;
  memo: string;
};

const normalizeText = (value: unknown): string => {
  return typeof value === 'string' ? value.trim() : '';
};

const parseCsv = (csvText: string): SchoolRecord[] => {
  return csvText
    .split(/\r?\n/)
    .slice(1)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [code = '', schoolName = '', location = ''] = line.split(',');
      return {
        code: code.trim(),
        schoolName: schoolName.trim(),
        location: location.trim(),
      };
    })
    .filter((item) => item.code && item.schoolName && item.location);
};

const getMemberKey = (member: AcademyMember, index: number): string => {
  const keyParts = [member.id, member.email, member.phone, member.uniqueKey].filter(Boolean);
  return keyParts.length > 0 ? keyParts.join('-') : `member-${index}`;
};

const getPhoneText = (phone: unknown): string => {
  const phoneText = normalizeText(phone);
  return phoneText || '-';
};

const sanitizePhone = (phone: string): string => {
  return phone.replace(/[^0-9+]/g, '');
};

const getRole = (member: AcademyMember): MemberRole | '' => {
  const roleValue = normalizeText(member.role).toLowerCase();
  if (roleValue === 'admin') {
    return 'admin';
  }

  if (roleValue === 'student') {
    return 'student';
  }

  return '';
};

const getTauserId = (member: AcademyMember): string => {
  const rawId = member.tauser_id ?? member.tauserId ?? member.id;

  if (typeof rawId === 'number') {
    return String(rawId);
  }

  return normalizeText(rawId);
};

const toEditableMember = (member: AcademyMember, index: number): EditableMember | null => {
  const role = getRole(member);
  if (!role) {
    return null;
  }

  const tauserId = getTauserId(member);

  return {
    key: getMemberKey(member, index),
    tauserId,
    role,
    email: normalizeText(member.email),
    name:
      normalizeText(member.name) ||
      normalizeText(member.username) ||
      normalizeText(member.email) ||
      `${role === 'admin' ? '관리자' : '학생'} ${index + 1}`,
    password: '',
    academyname:
      normalizeText(member.academyname) || normalizeText(member.academyName) || normalizeText(member.academy),
    phone: normalizeText(member.phone),
    parentPhone: normalizeText(member.parentPhone || member.parent_phone),
    memberClass: normalizeText(member.class),
    memo: normalizeText(member.memo),
    location: normalizeText(member.location),
    schoolName: normalizeText(member.schoolName),
    schoolCode: normalizeText(member.schoolCode),
  };
};

const extractErrorMessage = (error: any): string => {
  return String(
    error?.response?.data?.message || error?.response?.data || error?.message || '알 수 없는 오류'
  );
};

const AdminMembersPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState<AcademyMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);
  const [membersMessage, setMembersMessage] = useState('');
  const [schools, setSchools] = useState<SchoolRecord[]>([]);
  const [adminSortKey, setAdminSortKey] = useState<AdminSortKey>('name');
  const [adminSortDirection, setAdminSortDirection] = useState<SortDirection>('asc');
  const [studentSortKey, setStudentSortKey] = useState<StudentSortKey>('name');
  const [studentSortDirection, setStudentSortDirection] = useState<SortDirection>('asc');
  const [studentSearchQuery1, setStudentSearchQuery1] = useState('');
  const [studentSearchQuery2, setStudentSearchQuery2] = useState('');
  const [selectedMember, setSelectedMember] = useState<EditableMember | null>(null);
  const [editForm, setEditForm] = useState<EditableMember | null>(null);
  const [isUpdatingMember, setIsUpdatingMember] = useState(false);
  const [isPasswordEditing, setIsPasswordEditing] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [selectedAdminIds, setSelectedAdminIds] = useState<Set<string>>(new Set());
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<string>>(new Set());
  const [deletionProgress, setDeletionProgress] = useState<{ current: number; total: number } | null>(null);
  const [isDeletingMembers, setIsDeletingMembers] = useState(false);
  const academyName = getUserAcademyName(user);
  const displayName = getUserDisplayName(user);

  const locations = useMemo(
    () => Array.from(new Set(schools.map((school) => school.location))).sort((a, b) => a.localeCompare(b, 'ko')),
    [schools]
  );

  const filteredEditSchools = useMemo(
    () =>
      schools
        .filter((school) => school.location === (editForm?.location || ''))
        .sort((a, b) => a.schoolName.localeCompare(b.schoolName, 'ko')),
    [editForm?.location, schools]
  );

  const getSchoolCodeByNameAndLocation = useCallback(
    (location: string, schoolName: string) => {
      const matched = schools.find(
        (school) => school.location === location && school.schoolName === schoolName
      );
      return matched?.code || '';
    },
    [schools]
  );

  const adminMembers = useMemo<AdminMemberRow[]>(() => {
    return members
      .map((member, index) => {
        const editable = toEditableMember(member, index);
        if (!editable || editable.role !== 'admin') {
          return null;
        }

        return {
          editable,
          name: editable.name,
          phone: getPhoneText(editable.phone),
        };
      })
      .filter((member): member is AdminMemberRow => member !== null)
      .sort((left, right) => {
        const leftValue = left[adminSortKey];
        const rightValue = right[adminSortKey];
        const compared = leftValue.localeCompare(rightValue, 'ko');

        return adminSortDirection === 'asc' ? compared : compared * -1;
      });
  }, [adminSortDirection, adminSortKey, members]);

  const studentMembers = useMemo<StudentMemberRow[]>(() => {
    return members
      .map((member, index) => {
        const editable = toEditableMember(member, index);
        if (!editable || editable.role !== 'student') {
          return null;
        }

        return {
          editable,
          name: editable.name,
          schoolName: editable.schoolName || '-',
          memberClass: editable.memberClass || '-',
          memo: editable.memo || '-',
        };
      })
      .filter((member): member is StudentMemberRow => member !== null)
      .sort((left, right) => {
        const leftValue = left[studentSortKey];
        const rightValue = right[studentSortKey];
        const compared = leftValue.localeCompare(rightValue, 'ko');

        return studentSortDirection === 'asc' ? compared : compared * -1;
      });
  }, [members, studentSortDirection, studentSortKey]);

  const filteredStudentMembers = useMemo(() => {
    const q1 = studentSearchQuery1.trim().toLowerCase();
    const q2 = studentSearchQuery2.trim().toLowerCase();
    const matchesQuery = (m: StudentMemberRow, q: string): boolean => {
      if (!q) return true;
      return (
        m.name.toLowerCase().includes(q) ||
        m.schoolName.toLowerCase().includes(q) ||
        m.memberClass.toLowerCase().includes(q) ||
        m.memo.toLowerCase().includes(q)
      );
    };
    return studentMembers.filter((m) => matchesQuery(m, q1) && matchesQuery(m, q2));
  }, [studentMembers, studentSearchQuery1, studentSearchQuery2]);

  const selectedPhone = editForm ? sanitizePhone(editForm.phone) : '';
  const selectedParentPhone = editForm ? sanitizePhone(editForm.parentPhone) : '';
  const canCallSelected = selectedPhone.length > 0;
  const canCallSelectedParent = selectedParentPhone.length > 0;

  const loadMembers = useCallback(async () => {
    if (!academyName) {
      setMembers([]);
      setMembersMessage('관리자 계정에 학원명이 없어 회원 목록을 조회할 수 없습니다.');
      setIsLoadingMembers(false);
      return;
    }

    setIsLoadingMembers(true);

    try {
      setMembersMessage('');
      const response = await membersAPI.getAcademyMembers(academyName);
      setMembers(normalizeMembersPayload(response.data));
    } catch (error: any) {
      setMembersMessage(`회원 목록을 불러오지 못했습니다: ${extractErrorMessage(error)}`);
    } finally {
      setIsLoadingMembers(false);
    }
  }, [academyName]);

  useEffect(() => {
    const loadSchools = async () => {
      const response = await fetch(schoolsCsv);
      const csvText = await response.text();
      setSchools(parseCsv(csvText));
    };

    loadSchools().catch(() => undefined);
  }, []);

  useEffect(() => {
    loadMembers().catch(() => undefined);
  }, [loadMembers]);

  useEffect(() => {
    setEditForm((current) => {
      if (!current || current.role !== 'student') {
        return current;
      }

      if (current.schoolCode) {
        return current;
      }

      if (!current.location || !current.schoolName) {
        return current;
      }

      const inferredSchoolCode = getSchoolCodeByNameAndLocation(current.location, current.schoolName);
      if (!inferredSchoolCode) {
        return current;
      }

      return {
        ...current,
        schoolCode: inferredSchoolCode,
      };
    });
  }, [getSchoolCodeByNameAndLocation]);

  const handleAdminSort = (nextSortKey: AdminSortKey) => {
    if (adminSortKey === nextSortKey) {
      setAdminSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setAdminSortKey(nextSortKey);
    setAdminSortDirection('asc');
  };

  const handleStudentSort = (nextSortKey: StudentSortKey) => {
    if (studentSortKey === nextSortKey) {
      setStudentSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setStudentSortKey(nextSortKey);
    setStudentSortDirection('asc');
  };

  const getAdminSortIndicator = (columnKey: AdminSortKey): string => {
    if (adminSortKey !== columnKey) {
      return 'ㆍ';
    }

    return adminSortDirection === 'asc' ? '▲' : '▼';
  };

  const getStudentSortIndicator = (columnKey: StudentSortKey): string => {
    if (studentSortKey !== columnKey) {
      return 'ㆍ';
    }

    return studentSortDirection === 'asc' ? '▲' : '▼';
  };

  const handleSelectMember = (editable: EditableMember) => {
    const inferredSchoolCode =
      editable.schoolCode || getSchoolCodeByNameAndLocation(editable.location, editable.schoolName);

    const next = {
      ...editable,
      schoolCode: inferredSchoolCode,
      password: '',
    };

    setSelectedMember(next);
    setEditForm(next);
    setIsPasswordEditing(false);
    setUpdateMessage('');
  };

  const handlePasswordFieldClick = () => {
    if (isPasswordEditing) {
      return;
    }

    const confirmed = window.confirm('비밀번호를 수정합니까?');
    if (!confirmed) {
      return;
    }

    setIsPasswordEditing(true);
  };

  const handleCancelEdit = () => {
    setSelectedMember(null);
    setEditForm(null);
    setIsPasswordEditing(false);
    setUpdateMessage('');
  };

  const handleEditFieldChange = (field: keyof EditableMember, value: string) => {
    setEditForm((current) => {
      if (!current) {
        return current;
      }

      if (field === 'role') {
        const nextRole = value === 'admin' ? 'admin' : 'student';
        if (nextRole === 'admin') {
          return {
            ...current,
            role: nextRole,
            parentPhone: '',
            location: '',
            schoolName: '',
            schoolCode: '',
          };
        }

        return {
          ...current,
          role: nextRole,
        };
      }

      return {
        ...current,
        [field]: value,
      };
    });
  };

  const handleStudentLocationChange = (nextLocation: string) => {
    setEditForm((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        location: nextLocation,
        schoolName: '',
        schoolCode: '',
      };
    });
  };

  const handleStudentSchoolChange = (nextSchoolCode: string) => {
    setEditForm((current) => {
      if (!current) {
        return current;
      }

      const selectedSchool = filteredEditSchools.find((school) => school.code === nextSchoolCode);

      return {
        ...current,
        schoolCode: nextSchoolCode,
        schoolName: selectedSchool?.schoolName || '',
      };
    });
  };

  const handleUpdateSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!editForm) {
      return;
    }

    if (!editForm.tauserId) {
      setUpdateMessage('수정할 회원의 tauser_id를 찾을 수 없습니다.');
      return;
    }

    const payload: Record<string, unknown> = {
      role: editForm.role,
      name: editForm.name,
      academyname: editForm.academyname,
      phone: editForm.phone,
      parentPhone: editForm.role === 'student' ? editForm.parentPhone : '',
      parent_phone: editForm.role === 'student' ? editForm.parentPhone : '',
      class: editForm.memberClass,
      memo: editForm.memo,
      location: editForm.role === 'student' ? editForm.location : '',
      schoolName: editForm.role === 'student' ? editForm.schoolName : '',
      schoolCode: editForm.role === 'student' ? editForm.schoolCode : '',
      uniqueKey: `${editForm.phone}_${editForm.name}`,
    };

    const nextPassword = editForm.password.trim();
    if (nextPassword) {
      payload.password = nextPassword;
    }

    setIsUpdatingMember(true);
    setUpdateMessage('');

    try {
      await membersAPI.updateTauser(editForm.tauserId, payload);
      setUpdateMessage('회원 정보가 수정되었습니다.');
      setSelectedMember({ ...editForm, password: '' });
      setEditForm((current) => (current ? { ...current, password: '' } : current));
      setIsPasswordEditing(false);
      await loadMembers();
    } catch (error: any) {
      setUpdateMessage(`회원 정보 수정 실패: ${extractErrorMessage(error)}`);
    } finally {
      setIsUpdatingMember(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  const handleToggleAdminCheckbox = (tauserId: string) => {
    setSelectedAdminIds((prev) => {
      const next = new Set(prev);
      if (next.has(tauserId)) {
        next.delete(tauserId);
      } else {
        next.add(tauserId);
      }
      return next;
    });
  };

  const handleToggleStudentCheckbox = (tauserId: string) => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      if (next.has(tauserId)) {
        next.delete(tauserId);
      } else {
        next.add(tauserId);
      }
      return next;
    });
  };

  const handleSelectAllAdmins = () => {
    if (selectedAdminIds.size === adminMembers.length) {
      setSelectedAdminIds(new Set());
    } else {
      setSelectedAdminIds(new Set(adminMembers.map((m) => m.editable.tauserId)));
    }
  };

  const handleSelectAllStudents = () => {
    if (selectedStudentIds.size === filteredStudentMembers.length) {
      setSelectedStudentIds(new Set());
    } else {
      setSelectedStudentIds(new Set(filteredStudentMembers.map((m) => m.editable.tauserId)));
    }
  };

  const performBulkDelete = async (idsToDelete: string[], role: 'admin' | 'student') => {
    if (idsToDelete.length === 0) {
      return;
    }

    setIsDeletingMembers(true);
    setDeletionProgress({ current: 0, total: idsToDelete.length });

    let successCount = 0;
    let failedCount = 0;

    for (let i = 0; i < idsToDelete.length; i++) {
      try {
        await membersAPI.deleteTauser(idsToDelete[i]);
        successCount++;
      } catch (error: any) {
        failedCount++;
        console.error(`삭제 실패 (${idsToDelete[i]}):`, extractErrorMessage(error));
      }
      setDeletionProgress({ current: i + 1, total: idsToDelete.length });
    }

    setIsDeletingMembers(false);
    const message =
      failedCount === 0
        ? `${role === 'admin' ? '관리자' : '학생'} ${successCount}명이 삭제되었습니다.`
        : `${successCount}명 삭제 성공, ${failedCount}명 삭제 실패`;
    setMembersMessage(message);
    setDeletionProgress(null);
    await loadMembers();

    if (role === 'admin') {
      setSelectedAdminIds(new Set());
    } else {
      setSelectedStudentIds(new Set());
    }
  };

  const handleBulkDeleteAdmins = () => {
    if (selectedAdminIds.size === 0) {
      setMembersMessage('삭제할 관리자를 선택해주세요.');
      return;
    }

    const selectedNames = adminMembers
      .filter((m) => selectedAdminIds.has(m.editable.tauserId))
      .map((m) => m.name)
      .join(', ');

    const confirmMessage = `다음 관리자를 정말 삭제하시겠습니까?\n\n${selectedNames}`;
    if (window.confirm(confirmMessage)) {
      performBulkDelete(Array.from(selectedAdminIds), 'admin').catch(() => undefined);
    }
  };

  const handleBulkDeleteStudents = () => {
    if (selectedStudentIds.size === 0) {
      setMembersMessage('삭제할 학생을 선택해주세요.');
      return;
    }

    const selectedNames = filteredStudentMembers
      .filter((m) => selectedStudentIds.has(m.editable.tauserId))
      .map((m) => m.name)
      .join(', ');

    const confirmMessage = `다음 학생을 정말 삭제하시겠습니까?\n\n${selectedNames}`;
    if (window.confirm(confirmMessage)) {
      performBulkDelete(Array.from(selectedStudentIds), 'student').catch(() => undefined);
    }
  };

  return (
    <main className="auth-page">
      <section className="auth-card admin-card">
        <div className="admin-header-row">
          <div>
            <p className="auth-eyebrow">MEMBER MANAGEMENT</p>
            <h1>{displayName} 관리자님, 반갑습니다</h1>
            <p className="auth-description">
              같은 학원명으로 가입한 회원 목록을 확인하고, 필요할 때 직접 갱신할 수 있습니다.
            </p>
          </div>
          <div className="admin-action-row">
            <button
              type="button"
              className="auth-primary-button admin-refresh-button"
              onClick={() => {
                loadMembers().catch(() => undefined);
              }}
              disabled={isLoadingMembers}
            >
              {isLoadingMembers ? '갱신 중...' : '명단 갱신'}
            </button>
            <button
              type="button"
              className="auth-secondary-button admin-nav-button"
              onClick={() => navigate('/admin/home')}
            >
              관리자 홈
            </button>
            <button type="button" className="auth-secondary-button admin-logout-button" onClick={handleLogout}>
              로그아웃
            </button>
          </div>
        </div>

        <div className="admin-summary-grid">
          <div className="admin-summary-item">
            <span>학원명</span>
            <strong>{academyName || '-'}</strong>
          </div>
          <div className="admin-summary-item">
            <span>관리자 수</span>
            <strong>{adminMembers.length}</strong>
          </div>
          <div className="admin-summary-item">
            <span>학생 수</span>
            <strong>{studentMembers.length}</strong>
          </div>
        </div>

        {membersMessage ? <p className="auth-inline-message">{membersMessage}</p> : null}

        {isLoadingMembers ? (
          <p className="auth-inline-message">회원 목록을 불러오는 중입니다...</p>
        ) : (
          <div className="admin-table-stack">
            <section className="admin-table-section">
              <div className="admin-table-section-header">
                <h2>관리자 테이블</h2>
                <span>{adminMembers.length}명</span>
              </div>
              <div className="admin-table-shell">
                {adminMembers.length > 0 ? (
                  <>
                    <table className="admin-member-table">
                      <thead>
                        <tr>
                          <th className="admin-checkbox-column">
                            <input
                              type="checkbox"
                              checked={selectedAdminIds.size === adminMembers.length && adminMembers.length > 0}
                              onChange={handleSelectAllAdmins}
                              disabled={isDeletingMembers}
                              aria-label="모든 관리자 선택"
                            />
                          </th>
                          <th>
                            <button
                              type="button"
                              className="admin-sort-button"
                              onClick={() => handleAdminSort('name')}
                            >
                              이름 <span>{getAdminSortIndicator('name')}</span>
                            </button>
                          </th>
                          <th>
                            <button
                              type="button"
                              className="admin-sort-button"
                              onClick={() => handleAdminSort('phone')}
                            >
                              전화번호 <span>{getAdminSortIndicator('phone')}</span>
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminMembers.map((member) => (
                          <tr
                            key={member.editable.key}
                            className={
                              selectedMember?.key === member.editable.key
                                ? 'admin-clickable-row selected-row'
                                : 'admin-clickable-row'
                            }
                          >
                            <td className="admin-checkbox-column">
                              <input
                                type="checkbox"
                                checked={selectedAdminIds.has(member.editable.tauserId)}
                                onChange={() => handleToggleAdminCheckbox(member.editable.tauserId)}
                                disabled={isDeletingMembers}
                                aria-label={`${member.name} 선택`}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td onClick={() => handleSelectMember(member.editable)}>{member.name}</td>
                            <td onClick={() => handleSelectMember(member.editable)}>{member.phone}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {selectedAdminIds.size > 0 && (
                      <div className="admin-action-bar">
                        {deletionProgress ? (
                          <div className="admin-deletion-progress">
                            <div className="progress-info">
                              삭제 중... ({deletionProgress.current} / {deletionProgress.total})
                            </div>
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{
                                  width: `${(deletionProgress.current / deletionProgress.total) * 100}%`,
                                }}
                              ></div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <span className="admin-selection-info">
                              {selectedAdminIds.size}명 선택됨
                            </span>
                            <button
                              type="button"
                              className="auth-danger-button admin-bulk-delete-button"
                              onClick={handleBulkDeleteAdmins}
                              disabled={isDeletingMembers}
                            >
                              선택 항목 삭제
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="auth-inline-message">같은 학원명의 관리자가 아직 없습니다.</p>
                )}
              </div>
            </section>

            <section className="admin-table-section">
              <div className="admin-table-section-header">
                <h2>학생 테이블</h2>
                <span>
                  {(studentSearchQuery1.trim() || studentSearchQuery2.trim())
                    ? `${filteredStudentMembers.length} / ${studentMembers.length}명`
                    : `${studentMembers.length}명`}
                </span>
              </div>
              <div className="admin-search-row">
                <div className="admin-search-bar">
                  <input
                    type="text"
                    className="admin-search-input"
                    placeholder="검색어 1 (이름·학교·반·메모)"
                    value={studentSearchQuery1}
                    onChange={(e) => setStudentSearchQuery1(e.target.value)}
                  />
                  {studentSearchQuery1 && (
                    <button
                      type="button"
                      className="admin-search-clear"
                      onClick={() => setStudentSearchQuery1('')}
                      aria-label="검색어 1 지우기"
                    >
                      ✕
                    </button>
                  )}
                </div>
                <div className="admin-search-bar">
                  <input
                    type="text"
                    className="admin-search-input"
                    placeholder="검색어 2 (이름·학교·반·메모)"
                    value={studentSearchQuery2}
                    onChange={(e) => setStudentSearchQuery2(e.target.value)}
                  />
                  {studentSearchQuery2 && (
                    <button
                      type="button"
                      className="admin-search-clear"
                      onClick={() => setStudentSearchQuery2('')}
                      aria-label="검색어 2 지우기"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="admin-table-shell">
                {filteredStudentMembers.length > 0 ? (
                  <>
                    <table className="admin-member-table">
                      <thead>
                        <tr>
                          <th className="admin-checkbox-column">
                            <input
                              type="checkbox"
                              checked={selectedStudentIds.size === filteredStudentMembers.length && filteredStudentMembers.length > 0}
                              onChange={handleSelectAllStudents}
                              disabled={isDeletingMembers}
                              aria-label="모든 학생 선택"
                            />
                          </th>
                          <th>
                            <button
                              type="button"
                              className="admin-sort-button"
                              onClick={() => handleStudentSort('name')}
                            >
                              학생명 <span>{getStudentSortIndicator('name')}</span>
                            </button>
                          </th>
                          <th>
                            <button
                              type="button"
                              className="admin-sort-button"
                              onClick={() => handleStudentSort('schoolName')}
                            >
                              학교 <span>{getStudentSortIndicator('schoolName')}</span>
                            </button>
                          </th>
                          <th>
                            <button
                              type="button"
                              className="admin-sort-button"
                              onClick={() => handleStudentSort('memberClass')}
                            >
                              반편성 <span>{getStudentSortIndicator('memberClass')}</span>
                            </button>
                          </th>
                          <th>
                            <button
                              type="button"
                              className="admin-sort-button"
                              onClick={() => handleStudentSort('memo')}
                            >
                              메모 <span>{getStudentSortIndicator('memo')}</span>
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudentMembers.map((member) => (
                          <tr
                            key={member.editable.key}
                            className={
                              selectedMember?.key === member.editable.key
                                ? 'admin-clickable-row selected-row'
                                : 'admin-clickable-row'
                            }
                          >
                            <td className="admin-checkbox-column">
                              <input
                                type="checkbox"
                                checked={selectedStudentIds.has(member.editable.tauserId)}
                                onChange={() => handleToggleStudentCheckbox(member.editable.tauserId)}
                                disabled={isDeletingMembers}
                                aria-label={`${member.name} 선택`}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td onClick={() => handleSelectMember(member.editable)}>{member.name}</td>
                            <td onClick={() => handleSelectMember(member.editable)}>{member.schoolName}</td>
                            <td onClick={() => handleSelectMember(member.editable)}>{member.memberClass}</td>
                            <td onClick={() => handleSelectMember(member.editable)}>{member.memo}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {selectedStudentIds.size > 0 && (
                      <div className="admin-action-bar">
                        {deletionProgress ? (
                          <div className="admin-deletion-progress">
                            <div className="progress-info">
                              삭제 중... ({deletionProgress.current} / {deletionProgress.total})
                            </div>
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{
                                  width: `${(deletionProgress.current / deletionProgress.total) * 100}%`,
                                }}
                              ></div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <span className="admin-selection-info">
                              {selectedStudentIds.size}명 선택됨
                            </span>
                            <button
                              type="button"
                              className="auth-danger-button admin-bulk-delete-button"
                              onClick={handleBulkDeleteStudents}
                              disabled={isDeletingMembers}
                            >
                              선택 항목 삭제
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="auth-inline-message">
                    {(studentSearchQuery1.trim() || studentSearchQuery2.trim())
                      ? '검색 결과가 없습니다.'
                      : '같은 학원명의 학생이 아직 없습니다.'}
                  </p>
                )}
              </div>
            </section>
          </div>
        )}

        {editForm ? (
          <section className="admin-detail-card">
            <div className="admin-detail-header">
              <h2>선택 회원 정보</h2>
              <span>{editForm.role === 'student' ? '학생' : '관리자'}</span>
            </div>

            <div className="admin-detail-meta">
              <p>
                <strong>이메일:</strong> {editForm.email || '-'}
              </p>
              <p>
                <strong>이름:</strong> {editForm.name}
              </p>
            </div>

            <div className="admin-contact-actions">
              <a
                className={canCallSelected ? 'auth-secondary-button admin-contact-button' : 'auth-secondary-button admin-contact-button disabled'}
                href={canCallSelected ? `tel:${selectedPhone}` : undefined}
                aria-disabled={!canCallSelected}
                onClick={(event) => {
                  if (!canCallSelected) {
                    event.preventDefault();
                  }
                }}
              >
                {editForm.role === 'student' ? '학생 전화걸기' : '전화걸기'}
              </a>

              {editForm.role === 'student' ? (
                <>
                  <a
                    className={canCallSelectedParent ? 'auth-secondary-button admin-contact-button' : 'auth-secondary-button admin-contact-button disabled'}
                    href={canCallSelectedParent ? `tel:${selectedParentPhone}` : undefined}
                    aria-disabled={!canCallSelectedParent}
                    onClick={(event) => {
                      if (!canCallSelectedParent) {
                        event.preventDefault();
                      }
                    }}
                  >
                    학부모 전화걸기
                  </a>
                  <a
                    className={canCallSelected ? 'auth-secondary-button admin-contact-button' : 'auth-secondary-button admin-contact-button disabled'}
                    href={canCallSelected ? `sms:${selectedPhone}` : undefined}
                    aria-disabled={!canCallSelected}
                    onClick={(event) => {
                      if (!canCallSelected) {
                        event.preventDefault();
                      }
                    }}
                  >
                    학생 문자하기
                  </a>
                  <a
                    className={canCallSelectedParent ? 'auth-secondary-button admin-contact-button' : 'auth-secondary-button admin-contact-button disabled'}
                    href={canCallSelectedParent ? `sms:${selectedParentPhone}` : undefined}
                    aria-disabled={!canCallSelectedParent}
                    onClick={(event) => {
                      if (!canCallSelectedParent) {
                        event.preventDefault();
                      }
                    }}
                  >
                    학부모 문자하기
                  </a>
                </>
              ) : (
                <a
                  className={canCallSelected ? 'auth-secondary-button admin-contact-button' : 'auth-secondary-button admin-contact-button disabled'}
                  href={canCallSelected ? `sms:${selectedPhone}` : undefined}
                  aria-disabled={!canCallSelected}
                  onClick={(event) => {
                    if (!canCallSelected) {
                      event.preventDefault();
                    }
                  }}
                >
                  문자하기
                </a>
              )}
            </div>

            <form className="auth-form admin-edit-form" onSubmit={handleUpdateSubmit}>
              <label className="auth-field">
                <span>역할</span>
                <select
                  value={editForm.role}
                  onChange={(event) => handleEditFieldChange('role', event.target.value)}
                >
                  <option value="admin">관리자</option>
                  <option value="student">학생</option>
                </select>
              </label>

              <label className="auth-field">
                <span>비밀번호 (변경 시 입력)</span>
                <input
                  type="password"
                  value={editForm.password}
                  readOnly={!isPasswordEditing}
                  onClick={handlePasswordFieldClick}
                  onChange={(event) => {
                    if (!isPasswordEditing) {
                      return;
                    }
                    handleEditFieldChange('password', event.target.value);
                  }}
                  placeholder={isPasswordEditing ? '새 비밀번호를 입력하세요' : '클릭해서 비밀번호 수정 여부를 선택하세요'}
                />
              </label>

              <label className="auth-field">
                <span>소속학원명</span>
                <input
                  type="text"
                  value={editForm.academyname}
                  onChange={(event) => handleEditFieldChange('academyname', event.target.value)}
                  required
                />
              </label>

              <label className="auth-field">
                <span>이름</span>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(event) => handleEditFieldChange('name', event.target.value)}
                  required
                />
              </label>

              <label className="auth-field">
                <span>{editForm.role === 'student' ? '핸드폰 번호(학생)' : '핸드폰 번호'}</span>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(event) => handleEditFieldChange('phone', event.target.value)}
                  required
                />
              </label>

              {editForm.role === 'student' ? (
                <label className="auth-field">
                  <span>핸드폰 번호(보호자)</span>
                  <input
                    type="tel"
                    value={editForm.parentPhone}
                    onChange={(event) => handleEditFieldChange('parentPhone', event.target.value)}
                    required
                  />
                </label>
              ) : null}

              {editForm.role === 'student' ? (
                <>
                  <label className="auth-field">
                    <span>학교 소재지역</span>
                    <select
                      value={editForm.location}
                      onChange={(event) => handleStudentLocationChange(event.target.value)}
                      required
                    >
                      <option value="">학교 소재지역을 선택하세요</option>
                      {locations.map((location) => (
                        <option key={location} value={location}>
                          {location}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="auth-field">
                    <span>학교</span>
                    <select
                      value={editForm.schoolCode}
                      onChange={(event) => handleStudentSchoolChange(event.target.value)}
                      disabled={!editForm.location}
                      required
                    >
                      <option value="">학교를 선택하세요</option>
                      {filteredEditSchools.map((school) => (
                        <option key={`${school.code}-${school.schoolName}`} value={school.code}>
                          {school.schoolName}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              ) : null}

              <label className="auth-field">
                <span>반편성</span>
                <input
                  type="text"
                  value={editForm.memberClass}
                  onChange={(event) => handleEditFieldChange('memberClass', event.target.value)}
                />
              </label>

              <label className="auth-field">
                <span>메모</span>
                <input
                  type="text"
                  value={editForm.memo}
                  onChange={(event) => handleEditFieldChange('memo', event.target.value)}
                />
              </label>

              {updateMessage ? <p className="auth-inline-message">{updateMessage}</p> : null}

              <div className="admin-edit-actions">
                <button
                  type="button"
                  className="auth-secondary-button admin-edit-cancel-button"
                  onClick={handleCancelEdit}
                  disabled={isUpdatingMember}
                >
                  취소(뒤로가기)
                </button>
                <button type="submit" className="auth-primary-button admin-edit-save-button" disabled={isUpdatingMember}>
                  {isUpdatingMember ? '수정 중...' : '회원정보 수정 저장'}
                </button>
              </div>
            </form>
          </section>
        ) : null}
      </section>
    </main>
  );
};

export default AdminMembersPage;
